<script setup lang="ts">
/**
 * AppDialog - 可复用弹窗组件, 基于 @headlessui/vue.
 * 自带焦点锁定 / 滚动锁定 / ARIA / 点击遮罩关闭 / ESC 关闭.
 *
 * 用法 (v-model 控制显隐):
 *   <AppDialog v-model="visible" title="提示" message="内容" />
 *
 * 自定义内容 / 底部按钮:
 *   <AppDialog v-model="visible" title="标题">
 *     <p>自定义内容</p>
 *     <template #footer>
 *       <button @click="...">自定义按钮</button>
 *     </template>
 *   </AppDialog>
 */
import { computed } from 'vue'
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  TransitionRoot,
  TransitionChild,
} from '@headlessui/vue'

const props = withDefaults(
  defineProps<{
    modelValue: boolean
    title?: string
    message?: string
    confirmText?: string
    cancelText?: string
    showCancel?: boolean
    variant?: 'primary' | 'danger' | 'amber'
    showProgress?: boolean
    progress?: number
    dismissible?: boolean
  }>(),
  {
    title: '',
    message: '',
    confirmText: '确定',
    cancelText: '取消',
    showCancel: false,
    variant: 'primary',
    showProgress: false,
    progress: 0,
    dismissible: true,
  },
)

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  confirm: []
  cancel: []
  close: []
}>()

const isOpen = computed({
  get: () => props.modelValue,
  set: (v) => emit('update:modelValue', v),
})

function close(): void {
  if (!props.dismissible) return
  emit('close')
  isOpen.value = false
}

function onConfirm(): void {
  emit('confirm')
}

function onCancel(): void {
  emit('cancel')
  close()
}

const confirmClass = computed(() => {
  switch (props.variant) {
    case 'danger':
      return 'bg-red-500 active:bg-red-600'
    case 'amber':
      return 'bg-amber-600 active:bg-amber-700'
    default:
      return 'bg-primary active:opacity-80'
  }
})
</script>

<template>
  <TransitionRoot appear :show="isOpen" as="template">
    <Dialog as="div" class="relative z-[90]" @close="close">
      <!-- 遮罩 -->
      <TransitionChild
        as="template"
        enter="transition duration-200 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="transition duration-150 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black/40" aria-hidden="true" />
      </TransitionChild>

      <!-- 居中容器 -->
      <div class="fixed inset-0 flex items-center justify-center px-6">
        <TransitionChild
          as="template"
          enter="transition duration-200 ease-out"
          enter-from="opacity-0 scale-95"
          enter-to="opacity-100 scale-100"
          leave="transition duration-150 ease-in"
          leave-from="opacity-100 scale-100"
          leave-to="opacity-0 scale-95"
        >
          <DialogPanel
            class="w-full max-w-xs rounded-2xl bg-white p-5 shadow-xl"
          >
            <DialogTitle
              v-if="title"
              class="text-base font-semibold text-gray-800"
            >
              {{ title }}
            </DialogTitle>

            <!-- 内容: 默认插槽优先, 其次 message -->
            <slot>
              <p class="mt-2 text-sm text-gray-500">{{ message }}</p>
            </slot>

            <!-- 进度条 -->
            <div v-if="showProgress" class="mt-3">
              <div class="h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
                <div
                  class="h-full rounded-full bg-primary transition-all duration-300"
                  :style="{ width: progress + '%' }"
                ></div>
              </div>
              <p class="mt-1 text-right text-xs text-gray-400">{{ Math.round(progress) }}%</p>
            </div>

            <!-- 底部按钮: 有进度条时隐藏按钮 -->
            <slot name="footer">
              <div v-if="!showProgress" class="mt-5 flex gap-2.5">
                <button
                  v-if="showCancel"
                  type="button"
                  class="flex-1 rounded-xl border border-gray-200 py-2.5 text-sm font-medium text-gray-600 transition-colors active:bg-gray-50 focus:outline-none"
                  @click="onCancel"
                >
                  {{ cancelText }}
                </button>
                <button
                  v-if="confirmText"
                  type="button"
                  class="flex-1 rounded-xl py-2.5 text-sm font-medium text-white transition-colors focus:outline-none"
                  :class="confirmClass"
                  @click="onConfirm"
                >
                  {{ confirmText }}
                </button>
              </div>
            </slot>
          </DialogPanel>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
